import React from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, Folder, Send, Layers, CheckSquare, CalendarDays, FileText, Kanban, Lock, Image as ImageIcon, Link2, Forward } from 'lucide-react';
import { EncryptedImage } from '../EncryptedAttachment';
import { highlightSegments, resultHaystackField } from '../../utils/chatSearchHighlight';
import { formatSize } from '../../utils/chatExportFormats';

/**
 * Compact body of a search result (PLAN D10) — deliberately NOT
 * MessageContent: results never decrypt a vault secret (label chip only, no
 * VaultShareCard), cards carry no Save/Play actions, and every text the
 * server matched on is highlighted.
 */

const SHARE_ICONS = { collection: Folder, request: Send, workspace: Layers, task: CheckSquare, appointment: CalendarDays, document: FileText, board: Kanban };
const THUMB_BOX = { maxWidth: 160, maxHeight: 110 };

export function Highlighted({ text, terms }) {
  return (
    <>
      {highlightSegments(text, terms).map((seg, i) =>
        seg.hit ? (
          <mark key={i} className="chat-search-hit">
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
    </>
  );
}

function shareWhen(md) {
  if (!md.start) return '';
  const d = new Date(md.start);
  if (Number.isNaN(d.getTime())) return '';
  return md.all_day
    ? d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })
    : d.toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function ShareChip({ m, terms }) {
  const { t } = useTranslation();
  const md = m.metadata || {};
  const type = m.kind === 'ref' ? md.type : md.share_type;
  const Icon = SHARE_ICONS[type] || Link2;
  let label;
  if (type === 'task') label = t('chat.search.sharedTask');
  else label = <Highlighted text={resultHaystackField(m) || t('chat.search.sharedItem')} terms={terms} />;
  return (
    <span className={`chat-search-chip chat-search-chip--${type || 'item'}`}>
      <Icon size={13} />
      {type === 'request' && md.method && <span className="chat-search-method">{md.method}</span>}
      <span className="chat-search-chip-label">{label}</span>
      {type === 'collection' && Number.isFinite(md.item_count) && <span className="chat-search-chip-meta">{md.item_count}</span>}
      {type === 'appointment' && <span className="chat-search-chip-meta">{shareWhen(md)}</span>}
    </span>
  );
}

export default function ChatSearchResultBody({ message, terms, channelId }) {
  const { t } = useTranslation();
  const m = message;
  const md = m.metadata || {};
  const body = typeof m.body === 'string' ? m.body : '';
  let main = null;

  if (m.kind === 'code') {
    main = (
      <pre className="chat-search-code">
        <Highlighted text={body.split('\n').slice(0, 6).join('\n')} terms={terms} />
      </pre>
    );
  } else if (m.kind === 'image') {
    main = md.enc ? (
      <div className="chat-search-thumb">
        <EncryptedImage channelId={m.channel_id || channelId} url={md.url} mime={md.mime} name={md.name} box={THUMB_BOX} />
      </div>
    ) : (
      <span className="chat-search-chip">
        <ImageIcon size={13} />
        <span className="chat-search-chip-label">
          <Highlighted text={md.name || t('chat.search.photo')} terms={terms} />
        </span>
      </span>
    );
  } else if (m.kind === 'file') {
    main = (
      <span className="chat-search-chip">
        <Paperclip size={13} />
        <span className="chat-search-chip-label">
          <Highlighted text={md.name || t('chat.search.attachment')} terms={terms} />
        </span>
        {Number.isFinite(md.size) && <span className="chat-search-chip-meta">{formatSize(md.size)}</span>}
      </span>
    );
  } else if (m.kind === 'share' || m.kind === 'ref') {
    main = <ShareChip m={m} terms={terms} />;
  } else if (m.kind === 'secret') {
    main = (
      <span className="chat-search-chip chat-search-chip--secret" data-testid="chat-search-secret">
        <Lock size={13} />
        <span className="chat-search-chip-label">
          <Highlighted text={md.label || t('chat.search.secretLabel')} terms={terms} />
        </span>
      </span>
    );
  }

  const showText = body && m.kind !== 'code' && m.kind !== 'secret';
  return (
    <div className="chat-search-body">
      {md.forwarded === true && (
        <span className="chat-search-forwarded">
          <Forward size={11} /> {t('chat.search.forwarded')}
        </span>
      )}
      {md.reply_to && (
        <div className="chat-search-reply">
          ↪ {md.reply_to.sender_name || ''}: {md.reply_to.body || ''}
        </div>
      )}
      {main}
      {showText && (
        <div className="chat-search-text">
          <Highlighted text={body} terms={terms} />
        </div>
      )}
    </div>
  );
}
